// Circle Geometry Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const circleGeometrySamples = {
  fundamental: [
    {
      id: 'circle_fund_01',
      problem: "Find the equation of a circle with center (0, 0) and radius 3",
      problemType: "circle_geometry",
      difficulty: "fundamental",
      description: "Circle centered at origin",
      graphData: {
        nodes: [
          {
            id: "circle",
            name: "Circle",
            type: "concepts",
            description: "A set of all points equidistant from a center point",
            value: "Locus of points at distance r from center",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "center",
            name: "Center",
            type: "concepts",
            description: "The fixed point from which all points on circle are equidistant",
            value: "(0, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The distance from center to any point on the circle",
            value: "3 units",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "standard_form",
            name: "Standard Form",
            type: "formulas",
            description: "Equation of circle: (x-h)² + (y-k)² = r²",
            value: "(x-h)² + (y-k)² = r²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equation_result",
            name: "Circle Equation",
            type: "values",
            description: "The specific equation for this circle",
            value: "x² + y² = 9",
            importance: 10,
            x: 0, y: 150, z: -15
          },
          {
            id: "identify_center",
            name: "Identify Center",
            type: "steps",
            description: "Extract center coordinates from problem",
            value: "Step 1: Center (0, 0)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "identify_radius",
            name: "Identify Radius",
            type: "steps",
            description: "Extract radius value from problem",
            value: "Step 2: Radius = 3",
            importance: 8,
            x: 250, y: -50, z: 0
          },
          {
            id: "substitute_values",
            name: "Substitute Values",
            type: "steps",
            description: "Plug values into standard form equation",
            value: "Step 3: (x-0)² + (y-0)² = 3²",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "write_equation",
            name: "Write Equation",
            type: "steps",
            description: "Write the complete circle equation",
            value: "Step 4: x² + y² = 9",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "circle", to: "center", type: "defined by", strength: 10 },
          { from: "circle", to: "radius", type: "defined by", strength: 10 },
          { from: "standard_form", to: "equation_result", type: "gives", strength: 10 },
          { from: "center", to: "equation_result", type: "used in", strength: 9 },
          { from: "radius", to: "equation_result", type: "used in", strength: 9 },
          { from: "circle", to: "identify_center", type: "requires", strength: 8 },
          { from: "identify_center", to: "identify_radius", type: "enables", strength: 8 },
          { from: "identify_radius", to: "substitute_values", type: "enables", strength: 9 },
          { from: "substitute_values", to: "write_equation", type: "leads to", strength: 10 },
          { from: "write_equation", to: "equation_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'circle_fund_02',
      problem: "Find the equation of a circle with center (2, 0) and radius 4",
      problemType: "circle_geometry",
      difficulty: "fundamental",
      description: "Circle with center on x-axis",
      graphData: {
        nodes: [
          {
            id: "circle",
            name: "Circle",
            type: "concepts",
            description: "A set of all points equidistant from a center point",
            value: "Locus of points at distance r from center",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "center",
            name: "Center",
            type: "concepts",
            description: "The fixed point from which all points on circle are equidistant",
            value: "(2, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The distance from center to any point on the circle",
            value: "4 units",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "standard_form",
            name: "Standard Form",
            type: "formulas",
            description: "Equation of circle: (x-h)² + (y-k)² = r²",
            value: "(x-h)² + (y-k)² = r²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equation_result",
            name: "Circle Equation",
            type: "values",
            description: "The specific equation for this circle",
            value: "(x-2)² + y² = 16",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "circle", to: "center", type: "defined by", strength: 10 },
          { from: "circle", to: "radius", type: "defined by", strength: 10 },
          { from: "standard_form", to: "equation_result", type: "gives", strength: 10 },
          { from: "center", to: "equation_result", type: "used in", strength: 9 },
          { from: "radius", to: "equation_result", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'circle_fund_03',
      problem: "Find the equation of a circle with center (0, -3) and radius 2",
      problemType: "circle_geometry",
      difficulty: "fundamental",
      description: "Circle with center on y-axis",
      graphData: {
        nodes: [
          {
            id: "circle",
            name: "Circle",
            type: "concepts",
            description: "A set of all points equidistant from a center point",
            value: "Locus of points at distance r from center",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "center",
            name: "Center",
            type: "concepts",
            description: "The fixed point from which all points on circle are equidistant",
            value: "(0, -3)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The distance from center to any point on the circle",
            value: "2 units",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "standard_form",
            name: "Standard Form",
            type: "formulas",
            description: "Equation of circle: (x-h)² + (y-k)² = r²",
            value: "(x-h)² + (y-k)² = r²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equation_result",
            name: "Circle Equation",
            type: "values",
            description: "The specific equation for this circle",
            value: "x² + (y+3)² = 4",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "circle", to: "center", type: "defined by", strength: 10 },
          { from: "circle", to: "radius", type: "defined by", strength: 10 },
          { from: "standard_form", to: "equation_result", type: "gives", strength: 10 },
          { from: "center", to: "equation_result", type: "used in", strength: 9 },
          { from: "radius", to: "equation_result", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'circle_fund_04',
      problem: "Find the equation of a circle with center (1, 1) and radius 1",
      problemType: "circle_geometry",
      difficulty: "fundamental",
      description: "Unit circle translated",
      graphData: {
        nodes: [
          {
            id: "circle",
            name: "Circle",
            type: "concepts",
            description: "A set of all points equidistant from a center point",
            value: "Locus of points at distance r from center",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "center",
            name: "Center",
            type: "concepts",
            description: "The fixed point from which all points on circle are equidistant",
            value: "(1, 1)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The distance from center to any point on the circle",
            value: "1 unit",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "standard_form",
            name: "Standard Form",
            type: "formulas",
            description: "Equation of circle: (x-h)² + (y-k)² = r²",
            value: "(x-h)² + (y-k)² = r²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equation_result",
            name: "Circle Equation",
            type: "values",
            description: "The specific equation for this circle",
            value: "(x-1)² + (y-1)² = 1",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "circle", to: "center", type: "defined by", strength: 10 },
          { from: "circle", to: "radius", type: "defined by", strength: 10 },
          { from: "standard_form", to: "equation_result", type: "gives", strength: 10 },
          { from: "center", to: "equation_result", type: "used in", strength: 9 },
          { from: "radius", to: "equation_result", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'circle_fund_05',
      problem: "Find the equation of a circle with center (-2, 2) and radius 3",
      problemType: "circle_geometry",
      difficulty: "fundamental",
      description: "Circle in second quadrant",
      graphData: {
        nodes: [
          {
            id: "circle",
            name: "Circle",
            type: "concepts",
            description: "A set of all points equidistant from a center point",
            value: "Locus of points at distance r from center",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "center",
            name: "Center",
            type: "concepts",
            description: "The fixed point from which all points on circle are equidistant",
            value: "(-2, 2)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The distance from center to any point on the circle",
            value: "3 units",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "standard_form",
            name: "Standard Form",
            type: "formulas",
            description: "Equation of circle: (x-h)² + (y-k)² = r²",
            value: "(x-h)² + (y-k)² = r²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equation_result",
            name: "Circle Equation",
            type: "values",
            description: "The specific equation for this circle",
            value: "(x+2)² + (y-2)² = 9",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "circle", to: "center", type: "defined by", strength: 10 },
          { from: "circle", to: "radius", type: "defined by", strength: 10 },
          { from: "standard_form", to: "equation_result", type: "gives", strength: 10 },
          { from: "center", to: "equation_result", type: "used in", strength: 9 },
          { from: "radius", to: "equation_result", type: "used in", strength: 9 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'circle_inter_01',
      problem: "Find the center and radius of the circle x² + y² - 4x + 6y + 9 = 0",
      problemType: "circle_geometry",
      difficulty: "intermediate",
      description: "Convert from general form to standard form",
      graphData: {
        nodes: [
          {
            id: "general_form",
            name: "General Form",
            type: "concepts",
            description: "x² + y² + Dx + Ey + F = 0",
            value: "x² + y² - 4x + 6y + 9 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "completing_square",
            name: "Completing the Square",
            type: "concepts",
            description: "Method to convert to standard form",
            value: "Group x and y terms, complete squares",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "center_formula",
            name: "Center Formula",
            type: "formulas",
            description: "Center = (-D/2, -E/2)",
            value: "Center = (2, -3)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "radius_formula",
            name: "Radius Formula",
            type: "formulas",
            description: "r = √(D²/4 + E²/4 - F)",
            value: "r = √(4 + 9 - 9) = 2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "center_result",
            name: "Center",
            type: "values",
            description: "The center of the circle",
            value: "(2, -3)",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "radius_result",
            name: "Radius",
            type: "values",
            description: "The radius of the circle",
            value: "2 units",
            importance: 9,
            x: 120, y: 100, z: -15
          }
        ],
        links: [
          { from: "general_form", to: "completing_square", type: "requires", strength: 9 },
          { from: "completing_square", to: "center_formula", type: "gives", strength: 9 },
          { from: "completing_square", to: "radius_formula", type: "gives", strength: 9 },
          { from: "center_formula", to: "center_result", type: "equals", strength: 10 },
          { from: "radius_formula", to: "radius_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'circle_inter_02',
      problem: "Find the equation of a circle passing through points (0,0), (4,0), and (0,4)",
      problemType: "circle_geometry",
      difficulty: "intermediate",
      description: "Circle through three points",
      graphData: {
        nodes: [
          {
            id: "three_points",
            name: "Three Points",
            type: "concepts",
            description: "Points that lie on the circle",
            value: "(0,0), (4,0), (0,4)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "perpendicular_bisector",
            name: "Perpendicular Bisector",
            type: "concepts",
            description: "Method to find center",
            value: "Intersection of perpendicular bisectors",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "center",
            name: "Center",
            type: "values",
            description: "Center of the circle",
            value: "(2, 2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "d = √[(x₂-x₁)² + (y₂-y₁)²]",
            value: "Calculate radius using distance",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "radius",
            name: "Radius",
            type: "values",
            description: "Distance from center to any point",
            value: "2√2 units",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "equation",
            name: "Circle Equation",
            type: "values",
            description: "The equation of the circle",
            value: "(x-2)² + (y-2)² = 8",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "three_points", to: "perpendicular_bisector", type: "requires", strength: 9 },
          { from: "perpendicular_bisector", to: "center", type: "gives", strength: 10 },
          { from: "center", to: "distance_formula", type: "used in", strength: 8 },
          { from: "distance_formula", to: "radius", type: "gives", strength: 9 },
          { from: "center", to: "equation", type: "used in", strength: 9 },
          { from: "radius", to: "equation", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'circle_inter_03',
      problem: "Find the equation of a circle tangent to the x-axis at (3,0) with radius 3",
      problemType: "circle_geometry",
      difficulty: "intermediate",
      description: "Circle tangent to coordinate axis",
      graphData: {
        nodes: [
          {
            id: "tangent_point",
            name: "Tangent Point",
            type: "concepts",
            description: "Point where circle touches x-axis",
            value: "(3, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "radius_perpendicular",
            name: "Radius Perpendicular",
            type: "concepts",
            description: "Radius is perpendicular to tangent",
            value: "Center must be above x-axis",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "center",
            name: "Center",
            type: "values",
            description: "Center coordinates",
            value: "(3, 3)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "radius",
            name: "Radius",
            type: "values",
            description: "Given radius",
            value: "3 units",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "equation",
            name: "Circle Equation",
            type: "values",
            description: "The equation of the circle",
            value: "(x-3)² + (y-3)² = 9",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "tangent_point", to: "radius_perpendicular", type: "implies", strength: 9 },
          { from: "radius_perpendicular", to: "center", type: "determines", strength: 10 },
          { from: "center", to: "equation", type: "used in", strength: 9 },
          { from: "radius", to: "equation", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'circle_inter_04',
      problem: "Find the center and radius of the circle x² + y² + 6x - 8y + 16 = 0",
      problemType: "circle_geometry",
      difficulty: "intermediate",
      description: "Convert general form with larger coefficients",
      graphData: {
        nodes: [
          {
            id: "general_form",
            name: "General Form",
            type: "concepts",
            description: "x² + y² + Dx + Ey + F = 0",
            value: "x² + y² + 6x - 8y + 16 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "completing_square",
            name: "Completing the Square",
            type: "concepts",
            description: "Method to convert to standard form",
            value: "Group x and y terms, complete squares",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "center_formula",
            name: "Center Formula",
            type: "formulas",
            description: "Center = (-D/2, -E/2)",
            value: "Center = (-3, 4)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "radius_formula",
            name: "Radius Formula",
            type: "formulas",
            description: "r = √(D²/4 + E²/4 - F)",
            value: "r = √(9 + 16 - 16) = 3",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "center_result",
            name: "Center",
            type: "values",
            description: "The center of the circle",
            value: "(-3, 4)",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "radius_result",
            name: "Radius",
            type: "values",
            description: "The radius of the circle",
            value: "3 units",
            importance: 9,
            x: 120, y: 100, z: -15
          }
        ],
        links: [
          { from: "general_form", to: "completing_square", type: "requires", strength: 9 },
          { from: "completing_square", to: "center_formula", type: "gives", strength: 9 },
          { from: "completing_square", to: "radius_formula", type: "gives", strength: 9 },
          { from: "center_formula", to: "center_result", type: "equals", strength: 10 },
          { from: "radius_formula", to: "radius_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'circle_inter_05',
      problem: "Find the equation of a circle with center (1, -2) that passes through (4, 1)",
      problemType: "circle_geometry",
      difficulty: "intermediate",
      description: "Circle through a point with given center",
      graphData: {
        nodes: [
          {
            id: "center",
            name: "Center",
            type: "concepts",
            description: "Given center of the circle",
            value: "(1, -2)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point",
            name: "Point on Circle",
            type: "concepts",
            description: "Point that lies on the circle",
            value: "(4, 1)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "d = √[(x₂-x₁)² + (y₂-y₁)²]",
            value: "Calculate radius using distance",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "radius",
            name: "Radius",
            type: "values",
            description: "Distance from center to point",
            value: "√18 = 3√2 units",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "equation",
            name: "Circle Equation",
            type: "values",
            description: "The equation of the circle",
            value: "(x-1)² + (y+2)² = 18",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "center", to: "distance_formula", type: "used in", strength: 9 },
          { from: "point", to: "distance_formula", type: "used in", strength: 9 },
          { from: "distance_formula", to: "radius", type: "gives", strength: 10 },
          { from: "center", to: "equation", type: "used in", strength: 9 },
          { from: "radius", to: "equation", type: "used in", strength: 9 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'circle_hard_01',
      problem: "Find the equation of a circle that passes through (1,1), (3,1), and (2,3)",
      problemType: "circle_geometry",
      difficulty: "harder",
      description: "Circle through three non-collinear points",
      graphData: {
        nodes: [
          {
            id: "three_points",
            name: "Three Points",
            type: "concepts",
            description: "Points that lie on the circle",
            value: "(1,1), (3,1), (2,3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "perpendicular_bisector",
            name: "Perpendicular Bisector",
            type: "concepts",
            description: "Method to find center",
            value: "Intersection of perpendicular bisectors",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "center",
            name: "Center",
            type: "values",
            description: "Center of the circle",
            value: "(2, 2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "d = √[(x₂-x₁)² + (y₂-y₁)²]",
            value: "Calculate radius using distance",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "radius",
            name: "Radius",
            type: "values",
            description: "Distance from center to any point",
            value: "√2 units",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "equation",
            name: "Circle Equation",
            type: "values",
            description: "The equation of the circle",
            value: "(x-2)² + (y-2)² = 2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "three_points", to: "perpendicular_bisector", type: "requires", strength: 9 },
          { from: "perpendicular_bisector", to: "center", type: "gives", strength: 10 },
          { from: "center", to: "distance_formula", type: "used in", strength: 8 },
          { from: "distance_formula", to: "radius", type: "gives", strength: 9 },
          { from: "center", to: "equation", type: "used in", strength: 9 },
          { from: "radius", to: "equation", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'circle_hard_02',
      problem: "Find the center and radius of the circle x² + y² - 10x + 4y + 20 = 0",
      problemType: "circle_geometry",
      difficulty: "harder",
      description: "Complex general form conversion",
      graphData: {
        nodes: [
          {
            id: "general_form",
            name: "General Form",
            type: "concepts",
            description: "x² + y² + Dx + Ey + F = 0",
            value: "x² + y² - 10x + 4y + 20 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "completing_square",
            name: "Completing the Square",
            type: "concepts",
            description: "Method to convert to standard form",
            value: "Group x and y terms, complete squares",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "center_formula",
            name: "Center Formula",
            type: "formulas",
            description: "Center = (-D/2, -E/2)",
            value: "Center = (5, -2)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "radius_formula",
            name: "Radius Formula",
            type: "formulas",
            description: "r = √(D²/4 + E²/4 - F)",
            value: "r = √(25 + 4 - 20) = 3",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "center_result",
            name: "Center",
            type: "values",
            description: "The center of the circle",
            value: "(5, -2)",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "radius_result",
            name: "Radius",
            type: "values",
            description: "The radius of the circle",
            value: "3 units",
            importance: 9,
            x: 120, y: 100, z: -15
          }
        ],
        links: [
          { from: "general_form", to: "completing_square", type: "requires", strength: 9 },
          { from: "completing_square", to: "center_formula", type: "gives", strength: 9 },
          { from: "completing_square", to: "radius_formula", type: "gives", strength: 9 },
          { from: "center_formula", to: "center_result", type: "equals", strength: 10 },
          { from: "radius_formula", to: "radius_result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'circle_hard_03',
      problem: "Find the equation of a circle tangent to both x-axis and y-axis with radius 4",
      problemType: "circle_geometry",
      difficulty: "harder",
      description: "Circle tangent to both coordinate axes",
      graphData: {
        nodes: [
          {
            id: "tangent_axes",
            name: "Tangent to Axes",
            type: "concepts",
            description: "Circle touches both x and y axes",
            value: "Tangent to x-axis and y-axis",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "center_position",
            name: "Center Position",
            type: "concepts",
            description: "Center must be in first quadrant",
            value: "Center at (4, 4)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radius",
            name: "Radius",
            type: "values",
            description: "Given radius",
            value: "4 units",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "equation",
            name: "Circle Equation",
            type: "values",
            description: "The equation of the circle",
            value: "(x-4)² + (y-4)² = 16",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "tangent_axes", to: "center_position", type: "determines", strength: 10 },
          { from: "center_position", to: "equation", type: "used in", strength: 9 },
          { from: "radius", to: "equation", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'circle_hard_04',
      problem: "Find the equation of a circle that passes through (0,0) and is tangent to the line y = 2x at (1,2)",
      problemType: "circle_geometry",
      difficulty: "harder",
      description: "Circle through origin and tangent to line",
      graphData: {
        nodes: [
          {
            id: "origin_point",
            name: "Origin Point",
            type: "concepts",
            description: "Circle passes through origin",
            value: "(0, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "tangent_line",
            name: "Tangent Line",
            type: "concepts",
            description: "Circle tangent to line y = 2x",
            value: "Tangent at (1, 2)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "perpendicular_radius",
            name: "Perpendicular Radius",
            type: "concepts",
            description: "Radius perpendicular to tangent",
            value: "Center lies on perpendicular line",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "center",
            name: "Center",
            type: "values",
            description: "Center coordinates",
            value: "(2, 1)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "radius",
            name: "Radius",
            type: "values",
            description: "Distance from center to origin",
            value: "√5 units",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "equation",
            name: "Circle Equation",
            type: "values",
            description: "The equation of the circle",
            value: "(x-2)² + (y-1)² = 5",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "origin_point", to: "center", type: "constrains", strength: 9 },
          { from: "tangent_line", to: "perpendicular_radius", type: "implies", strength: 9 },
          { from: "perpendicular_radius", to: "center", type: "determines", strength: 10 },
          { from: "center", to: "radius", type: "used to calculate", strength: 9 },
          { from: "center", to: "equation", type: "used in", strength: 9 },
          { from: "radius", to: "equation", type: "used in", strength: 9 }
        ]
      }
    },
    {
      id: 'circle_hard_05',
      problem: "Find the equation of a circle with center (3, -1) that is tangent to the circle x² + y² = 16",
      problemType: "circle_geometry",
      difficulty: "harder",
      description: "Circle tangent to another circle",
      graphData: {
        nodes: [
          {
            id: "given_circle",
            name: "Given Circle",
            type: "concepts",
            description: "Circle centered at origin",
            value: "x² + y² = 16",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "center",
            name: "Center",
            type: "concepts",
            description: "Given center of new circle",
            value: "(3, -1)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_centers",
            name: "Distance Between Centers",
            type: "concepts",
            description: "Distance from (0,0) to (3,-1)",
            value: "√10 units",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "tangent_condition",
            name: "Tangent Condition",
            type: "concepts",
            description: "Sum or difference of radii equals distance",
            value: "r₁ + r₂ = d or |r₁ - r₂| = d",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "radius",
            name: "Radius",
            type: "values",
            description: "Radius of new circle",
            value: "√10 - 4 units",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "equation",
            name: "Circle Equation",
            type: "values",
            description: "The equation of the circle",
            value: "(x-3)² + (y+1)² = (√10 - 4)²",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "given_circle", to: "distance_centers", type: "constrains", strength: 9 },
          { from: "center", to: "distance_centers", type: "used in", strength: 9 },
          { from: "distance_centers", to: "tangent_condition", type: "used in", strength: 9 },
          { from: "tangent_condition", to: "radius", type: "determines", strength: 10 },
          { from: "center", to: "equation", type: "used in", strength: 9 },
          { from: "radius", to: "equation", type: "used in", strength: 9 }
        ]
      }
    }
  ]
}; 